import type { StateVersionSummary } from '@/lib/apiTypes'
import { relativeTime } from '@/lib/format'

/**
 * One serial in the rail. Compact by necessity - the rail is ~210px - so it carries only what you
 * scan a history for: which serial, how long ago, and whether it is clean. Everything else moves
 * to the pane header for the selected serial.
 */
export function SerialRailRow({ v, index, selected, onSelect }: {
  v: StateVersionSummary
  index: number
  selected: boolean
  onSelect: () => void
}) {
  const tone = v.critical_count > 0 ? 'var(--red)'
    : v.high_count > 0 ? 'var(--amber)'
    : v.scan_status === 'published' ? 'var(--green)'
    : 'var(--text3)'
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      style={{
        display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
        background: selected ? 'var(--bg3)' : 'transparent',
        // The left accent is the fix for selection being invisible: a background tint alone was
        // the ONLY selected-state signal before, and it reads as hover noise in a long list.
        borderLeft: `2px solid ${selected ? 'var(--accent)' : 'transparent'}`,
        borderTop: 'none', borderRight: 'none', borderBottom: '1px solid var(--border)',
        padding: '8px 10px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: '0.78rem', color: 'var(--text)' }}>{v.serial}</span>
        {index === 0 && <span className="badge badge-accent" style={{ fontSize: '0.5rem' }}>current</span>}
        <span style={{ flex: 1 }} />
        <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: tone, flexShrink: 0 }} />
      </div>
      <div style={{ fontSize: '0.64rem', color: 'var(--text3)', marginTop: 2 }}>
        {relativeTime(v.pushed_at)}
        {v.critical_count > 0 && <span style={{ color: 'var(--red)' }}> · {v.critical_count} critical</span>}
        {v.critical_count === 0 && v.high_count > 0 && <span style={{ color: 'var(--amber)' }}> · {v.high_count} high</span>}
      </div>
      {/* Restore provenance belongs in the LIST, not only on the selected serial's header: it is
          how you spot a rollback while scanning the history, and a serial whose contents came
          from an earlier one is exactly the row you need to notice without clicking it.
          A badge, matching `current` above it and the pane header's, so it reads as status
          rather than as more of the muted metadata line. Still on its own line: at ~210px it
          cannot sit beside `current` without wrapping, and a restored serial is often also the
          latest one, since promote writes a NEW serial rather than moving a pointer. */}
      {v.promoted_from_serial != null && (
        <div style={{ marginTop: 4 }}>
          <span
            className="badge badge-neutral"
            style={{ fontSize: '0.5rem' }}
            title={`This serial's state was restored from serial ${v.promoted_from_serial}`}
          >
            <span aria-hidden>&#8634;</span> restored from {v.promoted_from_serial}
          </span>
        </div>
      )}
    </button>
  )
}
